import React, { useState } from "react";
import { Sliders, X, RotateCcw } from "lucide-react";
import { MODELS, MODEL_KEYS, ModelBadge } from "./ModelBadge";
import { useModelSettings } from "../lib/settings";

/**
 * Per-model temperature & max_tokens controls.
 * - `mode='single'` shows only the selected `model`'s controls (compact).
 * - `mode='all'` shows all 4 models (used in Compare).
 * Triggered via a button; renders a floating panel.
 */
export default function ModelSettings({ mode = "single", model, models }) {
  const [open, setOpen] = useState(false);
  const { settings, update, reset } = useModelSettings();

  const targets = mode === "all" ? (models || MODEL_KEYS) : [model];
  if (mode === "single" && !model) return null;

  return (
    <>
      <button
        data-testid="open-settings-btn"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-heading font-bold border-2 border-slate-300 hover:border-slate-900 transition-colors bg-white text-slate-700"
        title="Model settings"
      >
        <Sliders size={14} strokeWidth={2.5} />
        Settings
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-30 bg-slate-900/20"
            onClick={() => setOpen(false)}
            data-testid="settings-backdrop"
          />
          <div
            className="fixed z-40 right-6 top-20 w-[360px] max-h-[80vh] overflow-y-auto brut-card p-5"
            data-testid="settings-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                <Sliders size={18} strokeWidth={2.5} /> Model controls
              </h3>
              <button
                data-testid="close-settings-btn"
                onClick={() => setOpen(false)}
                className="text-slate-500 hover:text-slate-900"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-5">
              {targets.map((mk) => {
                const m = MODELS[mk];
                const s = settings[mk];
                return (
                  <div key={mk} className="border-2 rounded-xl p-3" style={{ borderColor: m.border, backgroundColor: m.bg + "33" }}>
                    <div className="flex items-center justify-between mb-3">
                      <ModelBadge id={mk} size="sm" />
                      <button
                        data-testid={`reset-${mk}-btn`}
                        onClick={() => reset(mk)}
                        className="text-xs font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1"
                        title="Reset to defaults"
                      >
                        <RotateCcw size={11} /> Reset
                      </button>
                    </div>

                    {/* Temperature */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs uppercase tracking-widest font-bold text-slate-600">Temperature</label>
                        <span className="text-xs font-mono font-bold" style={{ color: m.border }} data-testid={`temp-${mk}-value`}>
                          {Number(s.temperature).toFixed(2)}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={s.temperature}
                        onChange={(e) => update(mk, { temperature: parseFloat(e.target.value) })}
                        data-testid={`temp-${mk}-slider`}
                        className="w-full accent-slate-900"
                      />
                      <div className="flex justify-between text-[10px] text-slate-400 font-bold">
                        <span>Precise</span><span>Balanced</span><span>Wild</span>
                      </div>
                    </div>

                    {/* Max tokens */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs uppercase tracking-widest font-bold text-slate-600">Max tokens</label>
                        <span className="text-xs font-mono font-bold" style={{ color: m.border }} data-testid={`maxtok-${mk}-value`}>
                          {s.max_tokens}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="256"
                        max="4000"
                        step="64"
                        value={s.max_tokens}
                        onChange={(e) => update(mk, { max_tokens: parseInt(e.target.value, 10) })}
                        data-testid={`maxtok-${mk}-slider`}
                        className="w-full accent-slate-900"
                      />
                      <div className="flex justify-between text-[10px] text-slate-400 font-bold">
                        <span>Short</span><span>Long</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 text-[11px] text-slate-500 leading-tight">
              Settings are saved per-model in your browser and sent with every request.
            </div>
          </div>
        </>
      )}
    </>
  );
}
